import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UnitDetail } from "@/pages/Unit";
import {
  Card,
  KorwilBadge,
  Pagination,
  SectionTitle,
  StatusBadge,
  TableShell,
  Td,
  Th,
  fmtDate,
  isLate,
} from "@/components/Shared";
import { cn } from "@/lib/utils";

const TONE = {
  red: "border-red-900/70 hover:border-red-500 text-red-300",
  amber: "border-amber-900/70 hover:border-amber-500 text-amber-300",
  orange: "border-orange-900/70 hover:border-orange-500 text-orange-300",
  blue: "border-blue-900/70 hover:border-blue-500 text-blue-300",
  purple: "border-purple-900/70 hover:border-purple-500 text-purple-300",
};

export default function Attention() {
  const { korwilFilter } = useAuth();
  const [sp, setSp] = useSearchParams();
  const kategori = sp.get("kategori") || "NO_ACTUAL_TECH";
  const [cats, setCats] = useState([]);
  const [res, setRes] = useState({ items: [], total: 0 });
  const [workload, setWorkload] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(() => sp.get("id") || null);

  // Deep-link dari Broadcast: buka langsung drawer detail transaksi di Perlu Tindakan.
  useEffect(() => {
    const id = sp.get("id");
    setSel(id || null);
  }, [sp]);

  useEffect(() => {
    api
      .get("/attention/summary", { params: { korwil: korwilFilter || undefined } })
      .then(({ data }) => setCats(data.kategori))
      .catch(() => {});
  }, [korwilFilter]);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get(`/attention/${kategori}`, {
        params: { korwil: korwilFilter || undefined, page, per_page: perPage },
      })
      .then(({ data }) => setRes(data))
      .catch(() => setRes({ items: [], total: 0 }))
      .finally(() => setLoading(false));
    if (kategori === "HIGH_WORKLOAD") {
      api
        .get("/workload", { params: { korwil: korwilFilter || undefined } })
        .then(({ data }) => setWorkload(data.items.filter((x) => x.unit_aktif > 5)))
        .catch(() => {});
    }
  }, [kategori, korwilFilter, page, perPage]);

  useEffect(load, [load]);

  return (
    <div data-testid="page-attention">
      <SectionTitle
        title="Perlu Tindakan"
        subtitle="Apa yang perlu perhatian Anda hari ini? Klik kategori untuk membuka record."
      />

      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9">
        {cats.map((c) => (
          <button
            key={c.key}
            data-testid={`attention-cat-${c.key.toLowerCase()}`}
            onClick={() => {
              setSp({ kategori: c.key });
              setPage(1);
            }}
            className={cn(
              "rounded-lg border bg-slate-900/80 p-3 text-left transition-colors",
              TONE[c.color],
              kategori === c.key && "ring-1 ring-blue-500 bg-blue-950/20"
            )}
          >
            <div className="text-[10px] uppercase leading-tight tracking-wider text-slate-400">
              {c.label}
            </div>
            <div className="mt-1.5 font-mono-data text-2xl font-semibold">
              {c.count.toLocaleString("id-ID")}
            </div>
          </button>
        ))}
      </div>

      {kategori === "HIGH_WORKLOAD" && workload.length > 0 && (
        <Card className="mb-4 p-4">
          <h3 className="mb-3 font-display text-base font-semibold text-white">
            Teknisi dengan Beban Tertinggi
          </h3>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {workload.slice(0, 12).map((w) => (
              <div
                key={w.kode_teknisi}
                data-testid={`workload-${w.kode_teknisi}`}
                className="flex items-center justify-between rounded border border-slate-800 bg-slate-900/60 px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm text-slate-100">{w.nama}</div>
                  <div className="font-mono-data text-[10px] text-slate-500">{w.kode_teknisi}</div>
                </div>
                <div className="flex items-center gap-2">
                  <KorwilBadge value={w.korwil} />
                  <span className="font-mono-data text-lg font-semibold text-purple-300">
                    {w.unit_aktif}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <TableShell testId="attention-table" loading={loading} empty={res.items.length === 0}>
        <thead>
          <tr>
            <Th>id_transaksi</Th>
            <Th>Sekolah</Th>
            <Th>Kabupaten</Th>
            <Th>Korwil</Th>
            <Th>Jadwal</Th>
            <Th>Teknisi Pusat</Th>
            <Th>Teknisi Aktual</Th>
            <Th>Match</Th>
            <Th>Status</Th>
          </tr>
        </thead>
        <tbody>
          {res.items.map((u) => (
            <tr
              key={u.id_transaksi}
              data-testid={`attention-row-${u.id_transaksi}`}
              onClick={() => setSel(u.id_transaksi)}
              className="cursor-pointer border-b border-slate-800/70 transition-colors hover:bg-slate-800/50"
            >
              <Td className="font-mono-data text-xs text-blue-300">{u.id_transaksi}</Td>
              <Td className="max-w-[200px] truncate text-slate-100">{u.sekolah}</Td>
              <Td className="text-xs">{u.kabupaten_kota}</Td>
              <Td><KorwilBadge value={u.korwil} /></Td>
              <Td className={`font-mono-data text-xs ${isLate(u) ? "text-red-300" : ""}`}>{fmtDate(u.jadwal)}</Td>
              <Td className="max-w-[130px] truncate text-xs">
                {u.teknisi_pusat_nama || <span className="text-slate-500">-</span>}
              </Td>
              <Td className="max-w-[130px] truncate text-xs">
                {u.ops?.aktual_nama || <span className="text-red-300">Belum ada</span>}
              </Td>
              <Td>{u.ops?.match_status ? <StatusBadge value={u.ops.match_status} /> : "-"}</Td>
              <Td><StatusBadge value={u.ops?.status} /></Td>
            </tr>
          ))}
        </tbody>
      </TableShell>
      <Pagination page={res.page || page} perPage={perPage} total={res.total} onPage={setPage} onPerPage={(n) => { setPerPage(n); setPage(1); }} testId="attention-pagination" />
      {sel && (
        <UnitDetail
          id={sel}
          onClose={() => {
            setSel(null);
            const next = new URLSearchParams(sp);
            next.delete("id");
            setSp(next, { replace: true });
          }}
          onSaved={load}
        />
      )}
    </div>
  );
}
